import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { AdminTable } from './AdminTable';
import { BrandEditModal } from './BrandEditModal';
import type { Manufacturer } from '../services/manufacturersService';

interface ManufacturersTableProps {
  manufacturers: Manufacturer[];
  onEdit: (manufacturer: Manufacturer) => void;
  isLoading?: boolean;
}

export function ManufacturersTable({
  manufacturers,
  onEdit,
  isLoading = false,
}: ManufacturersTableProps) {
  const [editBrandsManufacturer, setEditBrandsManufacturer] =
    useState<Manufacturer | null>(null);

  const columns = [
    {
      key: 'name',
      header: 'Manufacturer',
      sortable: true,
      render: (manufacturer: Manufacturer) => (
        <div className="font-medium">{manufacturer.name}</div>
      ),
    },
    {
      key: 'brands',
      header: 'Brands',
      render: (manufacturer: Manufacturer) => (
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {manufacturer.brands && manufacturer.brands.length > 0 ? (
              <>
                {manufacturer.brands.slice(0, 3).map((brand, idx) => (
                  <Badge key={idx} variant="neutral" className="text-xs">
                    {brand}
                  </Badge>
                ))}
                {manufacturer.brands.length > 3 && (
                  <Badge variant="neutral" className="text-xs">
                    +{manufacturer.brands.length - 3}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400">N/A</span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditBrandsManufacturer(manufacturer)}
            className="text-blue-600 hover:text-blue-700"
          >
            Edit
          </Button>
        </div>
      ),
    },
    {
      key: 'min_order_qty',
      header: 'Min Qty for Free Ship',
      render: (manufacturer: Manufacturer) => (
        <div className="text-sm">
          {manufacturer.min_order_qty ? (
            <span>{manufacturer.min_order_qty} frames</span>
          ) : (
            <span className="text-gray-400">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'free_ship_amt',
      header: 'Free Ship $ Threshold',
      render: (manufacturer: Manufacturer) => (
        <div className="text-sm">
          {manufacturer.free_ship_amt ? (
            `$${manufacturer.free_ship_amt.toFixed(2)}`
          ) : (
            <span className="text-gray-400">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'rep_name',
      header: 'Sales Rep',
      render: (manufacturer: Manufacturer) => (
        <div className="text-sm">
          <div className="text-gray-600">
            {manufacturer.rep_name || <span className="text-gray-400">N/A</span>}
          </div>
          <div className="text-xs text-gray-400 italic mt-0.5">
            Brand-level reps: Phase 2
          </div>
        </div>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (manufacturer: Manufacturer) => (
        <Badge variant={manufacturer.is_active ? 'success' : 'neutral'}>
          {manufacturer.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (manufacturer: Manufacturer) => (
        <Button size="sm" variant="ghost" onClick={() => onEdit(manufacturer)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminTable
        data={manufacturers}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No manufacturers found"
      />

      <BrandEditModal
        isOpen={!!editBrandsManufacturer}
        onClose={() => setEditBrandsManufacturer(null)}
        manufacturer={editBrandsManufacturer}
      />
    </>
  );
}
